import { Box, Flex, useBreakpointValue } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { FC } from "react";

import { CountryComponentWrapper } from "components/Common/CountryComponentWrapper";
import { MostPopularBadge, OFBadge } from "design_system/components/Badge";
import { OFText } from "design_system/components/OFText";
import { RegionType, StripePriceDto, StripeProductDto } from "graphql/generated";
import { colors } from "src/colors";
import { addCurrencyPrefix, extractNumberFromText, getCurrencyIcon, toTitleCase } from "src/utils";
import { checkCountry } from "src/utils/checkCountry";
import { formatDisplayPriceByRegion } from "src/utils/priceFormat";

import {
  calculateBundleSavePercent,
  calculateStrikeoutPrice,
  getDisplayVariationText
} from "./utils";

interface Props {
  variant: string;
  price: StripePriceDto;
  setValue: (name: string, value: unknown, config?: Object) => void;
  isChecked: boolean;
  product: Pick<StripeProductDto, "texts">;
  basePricePerBox?: number;
}

export const Variation: FC<Props> = ({
  variant,
  price,
  isChecked,
  product,
  basePricePerBox,
  setValue
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { t } = useTranslation(["price_selection", "shop"]);

  const unit = product?.texts?.unit_patient;

  const isPopular = price.metadata[`${variant}_popular`] || false;

  const display: string = price.metadata[`${variant}_display`] || "";

  const splitedDisplay = getDisplayVariationText(variant, display);

  const variantActualPriceDisplay = price.metadata[`${variant}_actual_price_display`] || "";

  const totalPriceDisplay = price.metadata.actual_price_display || "";

  const displayPricePerDose = addCurrencyPrefix(variantActualPriceDisplay);

  const savePercent =
    variant === "frequency" ? calculateBundleSavePercent(basePricePerBox, price) : null;

  const strikeoutPrice =
    variant === "frequency" ? calculateStrikeoutPrice(basePricePerBox, price) : null;

  const isBasePrice = strikeoutPrice === extractNumberFromText(totalPriceDisplay);

  return (
    <Flex
      onClick={() => {
        setValue("variant_selection", price.id, { shouldValidate: true });
      }}
      direction="column"
      justify="flex-start"
      align="flex-start"
      w="100%"
      position="relative"
      mx="auto"
      cursor="pointer"
      bg="white"
      px={6}
      py={4}
      borderRadius="md"
      boxShadow="md"
      borderWidth="2px"
      borderColor={isChecked ? colors.orange[400] : "none"}
      mb={["16px", "21px"]}
    >
      <Flex w="100%" gap="1rem" justify="space-between" align="center">
        <OFText textStyle="h6">
          {variant === "frequency" ? splitedDisplay?.[0]?.toLocaleLowerCase() : splitedDisplay?.[0]}
        </OFText>
        {isPopular && isPopular === "true" && (
          <MostPopularBadge textTransform="uppercase" size={isMobile ? "small" : "large"}>
            {t("price_selection:labels.most_popular")}
          </MostPopularBadge>
        )}
      </Flex>
      <OFText textStyle="body3" mt={2}>
        {splitedDisplay?.[1] || ""}
      </OFText>

      {variant === "frequency" && (
        <>
          <Flex justifyContent="flex-start" mt={2}>
            <OFText textStyle="h6">{formatDisplayPriceByRegion(displayPricePerDose)}</OFText>

            {unit && <OFText textStyle="table2">&nbsp;/{toTitleCase(unit)}</OFText>}
            <CountryComponentWrapper countries={[RegionType.Sg, RegionType.Jp]}>
              <OFText textStyle="table2" pl={2}>
                {checkCountry([RegionType.Sg]) ? t("shop:with-gst") : t("shop:with-vat")}
              </OFText>
            </CountryComponentWrapper>
          </Flex>
          <Box h={[12, 10]} />
          <Flex w="100%" justify="space-between" align="flex-end">
            {savePercent ? (
              <OFBadge>
                {t("price_selection:labels.save", { percent: savePercent, defaultValue: "" })}
              </OFBadge>
            ) : (
              // keep space-between
              <Box />
            )}

            <Box>
              {!isBasePrice && strikeoutPrice && (
                <OFText
                  textStyle="h6"
                  color={colors.grey[600]}
                  textDecoration="line-through"
                  align="right"
                >
                  {getCurrencyIcon()}
                  {formatDisplayPriceByRegion(strikeoutPrice?.toString())}
                </OFText>
              )}

              <Flex justify="flex-start" gap={2}>
                <OFText textStyle="table2" textTransform="uppercase">
                  {t("price_selection:labels.total-label")}
                </OFText>

                <OFText textStyle="h5">{totalPriceDisplay}</OFText>
              </Flex>
            </Box>
          </Flex>
        </>
      )}
    </Flex>
  );
};
